import { type Placeable, type Rng, type Vec3 } from '../shape.js';

export const TAU = Math.PI * 2;

/** Uniform point inside an axis-aligned box centred on the origin. */
export function inBox(target: Placeable, random: Rng, half: Vec3): void {
  target.x = (random() * 2 - 1) * half.x;
  target.y = (random() * 2 - 1) * half.y;
  target.z = (random() * 2 - 1) * half.z;
}

/** Uniform point on the *surface* of a box — hollow, so forms read as shells. */
export function onBox(target: Placeable, random: Rng, half: Vec3): void {
  inBox(target, random, half);
  const face = Math.floor(random() * 3);
  const side = random() < 0.5 ? -1 : 1;
  if (face === 0) {
    target.x = half.x * side;
  } else if (face === 1) {
    target.y = half.y * side;
  } else {
    target.z = half.z * side;
  }
}

/** Uniform point on a sphere of the given radius. */
export function onSphere(target: Placeable, random: Rng, radius: number): void {
  const cosTheta = random() * 2 - 1;
  const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
  const phi = random() * TAU;
  target.x = Math.cos(phi) * sinTheta * radius;
  target.y = cosTheta * radius;
  target.z = Math.sin(phi) * sinTheta * radius;
}

/** Uniform point in a flat disc on the XZ plane. */
export function onDisc(target: Placeable, random: Rng, radius: number): void {
  const r = Math.sqrt(random()) * radius;
  const angle = random() * TAU;
  target.x = Math.cos(angle) * r;
  target.y = 0;
  target.z = Math.sin(angle) * r;
}

/**
 * Point on a surface of revolution around the Y axis. `radiusAt(v)` returns the
 * radius at normalised height `v`, which makes vases, columns, trunks, bells and
 * hulls one-liners.
 */
export function revolve(
  target: Placeable,
  random: Rng,
  radiusAt: (v: number) => number,
  height: number,
): void {
  const v = random();
  const angle = random() * TAU;
  const r = radiusAt(v);
  target.x = Math.cos(angle) * r;
  target.y = v * height;
  target.z = Math.sin(angle) * r;
}

/** Point inside a capsule stretched between two points — limbs, beams, cables. */
export function alongSegment(
  target: Placeable,
  random: Rng,
  from: Vec3,
  to: Vec3,
  radius: number,
): void {
  const t = random();
  const spread = radius * Math.sqrt(random());
  const angle = random() * TAU;
  target.x = from.x + (to.x - from.x) * t + Math.cos(angle) * spread;
  target.y = from.y + (to.y - from.y) * t + Math.sin(angle) * spread * 0.6;
  target.z = from.z + (to.z - from.z) * t + Math.sin(angle) * spread;
}

/** Rotate a point about the Y axis in place. */
export function spinY(target: Placeable, angle: number): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const x = target.x;
  target.x = x * c - target.z * s;
  target.z = x * s + target.z * c;
}

/** Rotate a point about the Z axis in place — used to lean and tilt parts. */
export function spinZ(target: Placeable, angle: number): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const x = target.x;
  target.x = x * c - target.y * s;
  target.y = x * s + target.y * c;
}

/** Offset a point in place. */
export function shift(target: Placeable, dx: number, dy: number, dz: number): void {
  target.x += dx;
  target.y += dy;
  target.z += dz;
}

/** Smooth, seamless pseudo-noise from a sum of incommensurate sines. */
export function ridges(x: number, z: number): number {
  return (
    Math.sin(x * 0.31 + 1.7) * 0.5 +
    Math.sin(z * 0.27 - 0.6) * 0.5 +
    Math.sin((x + z) * 0.53 + 2.4) * 0.28 +
    Math.sin((x - z) * 0.71 - 1.1) * 0.22 +
    Math.sin(x * 1.37 + z * 0.91) * 0.12
  );
}

/** Deterministic value in [0, 1) from an integer — cheap per-index variety. */
export function hash01(n: number): number {
  const x = Math.sin(n * 12.9898 + 78.233) * 43_758.5453;
  return x - Math.floor(x);
}

/** Blend towards 1 as `value` climbs through [edge0, edge1]. */
export function ramp(value: number, edge0: number, edge1: number): number {
  if (edge1 === edge0) {
    return value < edge0 ? 0 : 1;
  }
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Pick one of `count` buckets from a random draw. */
export function bucket(random: Rng, count: number): number {
  return Math.min(count - 1, Math.floor(random() * count));
}
